import { useEffect, useState } from "react";
import type { LatestReading } from "@spf/contracts";

/**
 * How often the verdict is recomputed. A second is the smallest unit
 * formatDuration prints, so anything finer would redraw without changing a
 * word.
 */
const TICK_MS = 1_000;

export interface FreshnessVerdict {
  /** The reading's age now, or null when there is no reading to age. */
  ageSeconds: number | null;
  stale: boolean;
}

/**
 * How old the reading on screen is, kept current by the clock.
 *
 * The rule stays the server's: the threshold and the age it starts from both
 * arrive in the payload, so a second client does not have to reimplement
 * freshness (docs/REQUIREMENTS.md section 3). What is added here is only the
 * time that has passed since the server said it -- which the server cannot
 * know, and without which the number is a photograph rather than a clock.
 *
 * That difference is the whole point on a held-open connection. A stream that
 * dies without closing -- a handset moving between wifi and cellular, a NAT
 * idle timeout -- leaves the socket looking open and nothing arriving on it,
 * and a page that only restated the last age it was given would report
 * "updated 2 minutes ago" for hours. The server's verdict is never overturned
 * here, only ever escalated by the clock.
 *
 * A hook rather than something inside the one component that prints it,
 * because the sentence is not the only thing that has to know: the flow
 * diagram dims on the same verdict, and a diagram at full brightness under a
 * sentence saying the reading is stale contradicts itself in the cue a reader
 * takes in first. One clock, read by everything that shows the same reading.
 */
export function useFreshness(value: LatestReading | null): FreshnessVerdict {
  // A heartbeat restating the same reading carries a larger age, which is a
  // new measurement and worth restarting the clock from. See anchorKey.
  const key = anchorKey(value);
  const [now, setNow] = useState(() => Date.now());
  const [anchor, setAnchor] = useState(() => ({ key, at: Date.now() }));

  // Adjusted during render rather than in an effect, which is what React
  // prefers for state derived from a prop: a newly arrived reading must be
  // shown at its own age on the first paint, not one frame later at the
  // previous reading's.
  if (anchor.key !== key) setAnchor({ key, at: Date.now() });

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    // Cleared on unmount. Freshness is shown by two views, so a leaked timer
    // is one 1 Hz interval and one retained closure per navigation, in an app
    // meant to be left open for days.
    return () => clearInterval(id);
  }, []);

  // Math.max keeps a backwards clock correction from making a reading younger.
  return verdictFor(value, Math.max(0, Math.floor((now - anchor.at) / 1000)));
}

/**
 * What a re-anchor follows: the reading the server sent and the age it gave
 * it, both primitives. Not the object's identity -- a caller that rebuilt an
 * equal object every render would re-anchor every render, and re-anchoring
 * sets state, so the two would spin.
 */
function anchorKey(value: LatestReading | null): string {
  if (value === null) return "";
  return `${value.reading?.time ?? ""}|${value.ageSeconds ?? ""}`;
}

/** The server's answer, moved on by however long ago it gave it. */
function verdictFor(value: LatestReading | null, elapsed: number): FreshnessVerdict {
  if (value === null) return { ageSeconds: null, stale: false };
  if (value.ageSeconds === null) return { ageSeconds: null, stale: value.stale };

  const ageSeconds = value.ageSeconds + elapsed;
  return { ageSeconds, stale: value.stale || ageSeconds > value.staleAfterSeconds };
}
